import { trackAnalyticsEvent } from 'util/analytics';
import React, {
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useRouter } from 'next/router';
import useSWR from 'swr';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useInfiniteLoader, useProfile } from 'hooks';
import {
  useSchoolsApi,
  useOrganizationsApi,
  OrganizationSummary,
} from 'services/api';
import { ListOrganizationsResponse } from 'services/api/apis/organizations/dto/list-organizations.dto';
import {
  ListMyOrganizationsResponse,
  MyOrganizationRole,
} from 'services/api/apis/organizations/dto/my-organizations.dto';
import { Routes } from 'services/routes';
import { OrganizationEventsModal } from 'components/ui';
import { useMarkOrganizationFollowed } from 'state/followed-organizations/followed-organizations.state';
import {
  COLORS,
  FONT,
  NoResultsState,
  ErrorState,
  FilterChipDescriptor,
} from '../../shared';
import {
  OrgBrowseTile,
  OrgBrowseTileSkeleton,
  OrgBrowseRole,
} from './OrgBrowseTile';
import { OrgsFilterBar, OrgScope } from './OrgsFilterBar';

export interface OrgsBrowseProps {
  schoolId: string | null;
}

const PAGE_SIZE = 24;
const GRID_GAP = 32;

interface SelectedOrg {
  id: string;
  name: string;
  logoUrl: string;
  schoolSlug: string;
}

export const OrgsBrowse = ({ schoolId }: OrgsBrowseProps): ReactElement => {
  const router = useRouter();
  const schoolsApi = useSchoolsApi();
  const organizationsApi = useOrganizationsApi();
  const { profile } = useProfile();

  const [scope, setScope] = useState<OrgScope>('all');
  const [search, setSearch] = useState('');
  const [selectedOrg, setSelectedOrg] = useState<SelectedOrg | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [requestedIds, setRequestedIds] = useState<Set<string>>(new Set());
  const [joiningId, setJoiningId] = useState<string | null>(null);
  const markFollowed = useMarkOrganizationFollowed();

  useEffect(() => {
    trackAnalyticsEvent({ name: 'org_tab_viewed' });
  }, []);

  const { data: myOrgsData } = useSWR<ListMyOrganizationsResponse>(
    'orgs-browse/my-organizations',
    async () => {
      const res = await organizationsApi.getMyOrganizations();
      return res.success ? res.value : { organizations: [] };
    },
  );

  const roleMap = useMemo(() => {
    const map = new Map<string, MyOrganizationRole>();
    myOrgsData?.organizations.forEach(o => map.set(o.id, o.myRole));
    return map;
  }, [myOrgsData]);

  const fetchPage = useCallback(
    (currentSchoolId: string, cursor: string | null, searchVal: string) =>
      schoolsApi.listOrganizations(currentSchoolId, {
        cursor: cursor ?? undefined,
        pageSize: PAGE_SIZE,
        search: searchVal || null,
      }),
    [schoolsApi],
  );

  const rOrgs = useInfiniteLoader<
    ListOrganizationsResponse,
    [string, string | null, string]
  >((pageIndex, previousPageData) => {
    if (!schoolId) return null;
    if (pageIndex > 0 && !previousPageData?.cursor) return null;
    return [schoolId, previousPageData?.cursor ?? null, search];
  }, fetchPage);

  const allOrgs = useMemo(
    () => rOrgs.data?.flatMap(p => p?.organizations ?? []) ?? [],
    [rOrgs.data],
  );

  // The "My organizations" tab is backed by a dedicated server-side fetch so
  // its list and count are complete without having to scroll the full "all"
  // list into memory first.
  const {
    data: followedData,
    isValidating: followedValidating,
    mutate: mutateFollowed,
  } = useSWR<ListOrganizationsResponse>(
    schoolId ? ['orgs-browse/followed', schoolId, search] : null,
    async () => {
      const res = await schoolsApi.listOrganizations(schoolId as string, {
        followed: true,
        pageSize: 50,
        search: search || null,
      });
      return res.success
        ? res.value
        : { count: 0, cursor: null, organizations: [] };
    },
  );

  const refreshOrgLists = useCallback((): void => {
    void rOrgs.mutate();
    void mutateFollowed();
  }, [rOrgs, mutateFollowed]);

  const followedOrgs = useMemo(
    () => followedData?.organizations ?? [],
    [followedData],
  );

  const orgs = useMemo(
    () => (scope === 'following' ? followedOrgs : allOrgs),
    [allOrgs, followedOrgs, scope],
  );

  const isInitialLoading =
    scope === 'following'
      ? !followedData && followedValidating
      : !rOrgs.data && rOrgs.isValidating;
  const isError = Boolean(rOrgs.error);
  const hasFilters = search.trim() !== '' || scope === 'following';

  const resolveRole = (org: OrganizationSummary): OrgBrowseRole => {
    const role = roleMap.get(org.id);
    if (role) return role;
    if (org.isFollowed) return 'follower';
    return 'none';
  };

  const isPending = (org: OrganizationSummary): boolean => {
    if (org.membershipApprovalMode === 'auto_accept') return false;
    return requestedIds.has(org.id) || org.myMembershipStatus === 'pending';
  };

  const isSameSchool = (org: OrganizationSummary): boolean =>
    Boolean(profile?.school?.id && org.school?.id && profile.school.id === org.school.id);

  const openView = (org: OrganizationSummary): void => {
    const role = resolveRole(org);
    trackAnalyticsEvent({
      name: 'my_org_card_clicked',
      props: {
        role: role === 'follower' || role === 'none' ? 'member' : role,
        action: 'view',
      },
    });
    setSelectedOrg({
      id: org.id,
      name: org.name,
      logoUrl: org.logoUrl,
      // Org list payloads usually include school; fall back if omitted.
      schoolSlug: org.school?.slug ?? profile?.school?.slug ?? '',
    });
    setModalOpen(true);
  };

  const openManage = (org: OrganizationSummary): void => {
    const role = roleMap.get(org.id);
    trackAnalyticsEvent({
      name: 'my_org_card_clicked',
      props: { role: role === 'leader' ? 'leader' : 'admin', action: 'manage' },
    });
    void router.push(Routes.admin.organizationDetail(org.id));
  };

  const joinOrg = async (org: OrganizationSummary): Promise<void> => {
    if (joiningId) return;
    setJoiningId(org.id);
    const res = await organizationsApi.requestMembership(org.id);
    setJoiningId(null);
    if (res.success) {
      if (res.value.status === 'pending') {
        setRequestedIds(prev => {
          const next = new Set(prev);
          next.add(org.id);
          return next;
        });
      } else if (res.value.status === 'approved') {
        markFollowed(org.id, true);
      }
      refreshOrgLists();
    }
  };

  const activeChips = useMemo<FilterChipDescriptor[]>(() => {
    const chips: FilterChipDescriptor[] = [];
    if (scope === 'following') {
      chips.push({
        id: 'scope',
        label: 'My organizations',
        onRemove: () => setScope('all'),
      });
    }
    if (search.trim()) {
      chips.push({
        id: 'search',
        label: `"${search.trim()}"`,
        onRemove: () => setSearch(''),
      });
    }
    return chips;
  }, [scope, search]);

  const clearAll = (): void => {
    setScope('all');
    setSearch('');
  };

  const renderBody = (): ReactElement => {
    if (isInitialLoading) {
      return (
        <Box sx={gridSx}>
          {Array.from({ length: 6 }).map((_, i) => (
            <Box key={i} sx={cardWrapSx}>
              <OrgBrowseTileSkeleton />
            </Box>
          ))}
        </Box>
      );
    }

    if (isError) {
      return (
        <ErrorState
          title="Couldn't load organizations."
          body="Something went wrong fetching the organization directory. Check your connection and try again."
          onRetry={() => void rOrgs.mutate()}
        />
      );
    }

    if (orgs.length === 0) {
      return hasFilters ? (
        <NoResultsState
          title="No organizations match your filters."
          body="Try a different search, or switch back to all organizations."
          chips={activeChips}
          clearLabel="Clear all filters"
          onClear={clearAll}
        />
      ) : (
        <NoResultsState
          icon="groups"
          title="No organizations yet."
          body="There aren't any organizations to explore here just yet — check back soon."
        />
      );
    }

    return (
      <>
        <Box sx={gridSx}>
          {orgs.map(org => {
            const role = resolveRole(org);
            const pending = isPending(org);
            const sameSchool = isSameSchool(org);
            return (
              <Box key={org.id} sx={cardWrapSx}>
                <OrgBrowseTile
                  name={org.name}
                  logoUrl={org.logoUrl}
                  role={role}
                  isRequested={pending}
                  onView={() => openView(org)}
                  onManage={
                    sameSchool && (role === 'admin' || role === 'leader')
                      ? () => openManage(org)
                      : undefined
                  }
                  onJoin={
                    sameSchool && (role === 'follower' || role === 'none')
                      ? () => void joinOrg(org)
                      : undefined
                  }
                  joining={joiningId === org.id}
                />
              </Box>
            );
          })}
        </Box>
        {scope === 'all' && rOrgs.isValidating && (
          <Box display="flex" justifyContent="center" py="24px" width="100%">
            <CircularProgress size={28} sx={{ color: COLORS.navy }} />
          </Box>
        )}
      </>
    );
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '24px',
        width: '100%',
      }}
    >
      <Typography
        component="h1"
        sx={{
          fontFamily: FONT,
          fontWeight: 600,
          fontSize: { xs: '24px', md: '28px' },
          lineHeight: '36px',
          color: COLORS.headingDark,
          m: 0,
        }}
      >
        Explore Organizations
      </Typography>

      <OrgsFilterBar
        scope={scope}
        onScopeChange={setScope}
        allCount={rOrgs.data?.[0]?.count || undefined}
        followingCount={followedData?.count || undefined}
        search={search}
        onSearchChange={setSearch}
      />

      {renderBody()}

      {selectedOrg && (
        <OrganizationEventsModal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          organizationId={selectedOrg.id}
          organizationName={selectedOrg.name}
          organizationLogoUrl={selectedOrg.logoUrl}
          schoolSlug={selectedOrg.schoolSlug}
          onMembershipChange={() => {
            setRequestedIds(prev => {
              const next = new Set(prev);
              next.add(selectedOrg.id);
              return next;
            });
            refreshOrgLists();
          }}
        />
      )}
    </Box>
  );
};

const gridSx = {
  display: 'grid',
  gridTemplateColumns: {
    xs: '1fr',
    md: 'repeat(2, minmax(0, 1fr))',
    lg: 'repeat(3, minmax(0, 1fr))',
  },
  rowGap: { xs: 0, md: `${GRID_GAP}px` },
  columnGap: `${GRID_GAP}px`,
  width: { xs: 'calc(100% + 40px)', md: '100%' },
  mx: { xs: '-20px', md: 0 },
} as const;

const cardWrapSx = {
  width: '100%',
  maxWidth: '100%',
  '&:not(:first-of-type)': { mt: { xs: '-1px', md: 0 } },
} as const;
